import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  DocumentSnapshot,
  onSnapshot,
  orderBy,
  query,
  Query,
  Unsubscribe,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { NotificationType, Notification } from '../../../../global';
import { useLoadingValue } from '../utils';
import { docToJSON, firestore } from '../utils/firebase';
import { usePagination } from '../utils/usePagination';
import { useAuth } from './use-auth';
import { updateNotification } from '../db/notifications';
import dayjs, { Dayjs } from 'dayjs';

type NotificationsHook = {
  error: FirebaseError;
  hasMore: boolean;
  load: () => void;
  loading: boolean;
  reset: () => void;
  notifications: Notification[];
};

type NotificationsHookOptions = {
  direction?: 'asc' | 'desc';
  pageSize?: number;
  read?: boolean;
  type?: NotificationType;
};

type NotificationHook = {
  error: FirebaseError;
  loading: boolean;
  notification: Notification;
  markAsRead: () => void;
};

type NotificationHookOptions = {
  id?: string;
};

export const useNotifications = ({
  direction = 'asc',
  pageSize = 10,
  read,
  type,
}: NotificationsHookOptions): NotificationsHook => {
  const { id } = useAuth();
  const [notificationsQuery, setNotificationsQuery] = useState<Query>();
  const { error, hasMore, load, loading, reset, values } = usePagination(
    notificationsQuery,
    pageSize
  );

  useEffect(() => {
    if (id) {
      let newQuery = query(
        collection(firestore, `users/${id}/notifications`),
        orderBy('createdAt', direction)
      );
      if (read !== undefined) {
        newQuery = query(newQuery, where('read', '==', read));
      }
      if (type !== undefined) {
        newQuery = query(newQuery, where('type', '==', type));
      }
      setNotificationsQuery(newQuery);
    } else if (notificationsQuery) {
      setNotificationsQuery(undefined);
    }
  }, [id, direction, read, type]);

  return useMemo(
    () => ({
      error,
      hasMore,
      load,
      loading,
      reset,
      notifications: values
        ? values.map((v) => prepareNotification(docToJSON<Notification>(v)))
        : values,
    }),
    [error, hasMore, loading, values]
  );
};

export const useNotification = (id?: string): NotificationHook => {
  const { id: userId } = useAuth();
  const { error, loading, reset, setError, setValue, value, setLoading } =
    useLoadingValue<DocumentSnapshot, FirebaseError>();

  useEffect(() => {
    let unsubscribe: Unsubscribe;
    if (id) {
      setLoading(true);
      const ref = doc(firestore, `users/${userId}/notifications`, id);
      unsubscribe = onSnapshot(ref, setValue, setError);
    } else {
      setLoading(false);
    }
  }, [id]);

  const markAsRead = async () => {
    if (!id) return;
    try {
      setLoading(true);
      await updateNotification(userId, id, { read: true });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(err);
    }
  };

  return useMemo(
    () => ({
      error,
      loading,
      notification: value ? docToJSON<Notification>(value) : null,
      markAsRead,
    }),
    [error, loading, value]
  );
};

const prepareNotification = (notification: Notification): Notification => {
  return {
    ...notification,
    formattedCreatedAt: dayjs(notification.createdAt).format(
      'DD.MM.YYYY HH:mm'
    ),
  };
};
